import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    useMemo,
} from "react";
import { FaArrowLeft } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import { motion, AnimatePresence } from "framer-motion";
import { api, auth } from "salesive-api-axios";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";

export function AuthForm({ onSuccess, onSkip }) {
    const [view, setView] = useState("email"); // 'email', 'details', 'otp'
    const [otp, setOtp] = useState("");
    const [email, setEmail] = useState("");
    const [formData, setFormData] = useState({
        fullName: "",
        phoneNumber: "",
        country: "nigeria",
    });

    // Countries Data Query
    const { data: countriesData } = useQuery({
        queryKey: ["countries"],
        queryFn: async () => {
            const response = await api.get("/country/countries");
            return response.data?.data || [];
        },
        staleTime: Infinity, // Countries don't change often
    });

    const countries = useMemo(() => {
        if (!countriesData || countriesData.length === 0) return [];
        return countriesData.map((country) => ({
            name: country.name,
            shortName: country.shortName,
            value: country.shortName,
            phone: country.phone,
            emoji: country.emoji,
        }));
    }, [countriesData]);

    // Update country if available
    useEffect(() => {
        if (!countries.length) return;
        setFormData((prev) => {
            // Check if current country value is valid
            const isValid =
                prev.country && countries.some((c) => c.value === prev.country);
            if (isValid) return prev;

            // Try to find Nigeria
            const defaultCountry = countries.find(
                (c) =>
                    c.name.toLowerCase() === "nigeria" ||
                    c.shortName.toLowerCase() === "ng"
            );

            // If found, set as default, otherwise match closest or first
            if (defaultCountry) {
                return { ...prev, country: defaultCountry.value };
            }

            // Fallback to first available country if no match found
            if (countries.length > 0) {
                return { ...prev, country: countries[0].value };
            }

            return prev;
        });
    }, [countries]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [cooldown, setCooldown] = useState(0);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);

    // Surface a Google sign-in error passed back on the URL. The one-time
    // exchange code itself is redeemed globally at widget boot
    // (ensureShopperSession → redeemGoogleHandoff), so by the time this form
    // mounts a successful sign-in has already navigated away.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const urlError = params.get("error");
        if (!urlError) return;

        setError(urlError);
        const url = new URL(window.location.href);
        url.searchParams.delete("error");
        window.history.replaceState({}, "", url.toString());
    }, []);

    const handleGoogleLogin = async () => {
        setIsGoogleLoading(true);
        setError(null);
        try {
            const response = await api.get("auth/google?redirect=false");
            const url = response.data?.data?.url || response.data?.url;
            if (url) {
                window.location.href = url;
            } else {
                setError("Invalid response from server");
            }
        } catch (err) {
            setError("Failed to initiate Google login");
            console.error(err);
        } finally {
            setIsGoogleLoading(false);
        }
    };

    const [otpValues, setOtpValues] = useState(Array(6).fill(""));
    const otpRefs = useRef([]);

    // Cooldown timer
    useEffect(() => {
        let timer;
        if (cooldown > 0) {
            timer = setInterval(() => {
                setCooldown((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [cooldown]);

    // Auto-focus OTP input
    useEffect(() => {
        if (view === "otp") {
            // Small delay to allow animation to start/render
            const timer = setTimeout(() => {
                otpRefs.current[0]?.focus();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [view]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${String(mins).padStart(2, "0")}:${String(secs).padStart(
            2,
            "0"
        )}`;
    };

    // OTP Input Logic
    const handleOtpBoxChange = (index) => (e) => {
        const raw = e.target.value || "";
        const digit = raw.replace(/\D/g, "").slice(0, 1);
        setOtpValues((prev) => {
            const next = [...prev];
            next[index] = digit;
            return next;
        });
        if (digit && index < 5) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpBoxKeyDown = (index) => (e) => {
        if (e.key === "Backspace" && !otpValues[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };

    const handleOtpBoxPaste = (e) => {
        const text = (e.clipboardData?.getData("text") || "").replace(
            /\D/g,
            ""
        );
        if (!text) return;
        e.preventDefault();
        const digits = text.slice(0, 6).split("");
        setOtpValues((prev) => {
            const next = Array(6).fill("");
            for (let i = 0; i < digits.length; i++) next[i] = digits[i];
            return next;
        });
        const lastIndex = Math.min(digits.length - 1, 5);
        if (lastIndex >= 0) {
            otpRefs.current[lastIndex]?.focus();
        }
    };

    useEffect(() => {
        setOtp(otpValues.join(""));
    }, [otpValues]);

    useEffect(() => {
        if (otp.length === 6) {
            handleVerifyOtp();
        }
    }, [otp]);

    const handleCheckUser = async (e) => {
        e.preventDefault();
        if (!email.trim()) {
            setError("Email is required");
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const res = await auth.checkUser({ email });
            // API response structure: data: { data: { exists: boolean } } based on reference
            // Reference: setAuthData(data.data.data); if (data.data.data) ...
            // Wait, reference says: setAuthData(data.data.data); if (data.data.data) -> localStorage... -> authUserMutation()
            // But logic inside render says: {authData && authData.exists === false && ( ... inputs ... )}

            // Re-reading reference:
            // onSuccess: (data) => { setAuthData(data.data.data); if (data.data.data) { ... authUserMutation() } else { setButtonText("Sign Up"); } }
            // Wait, if data.data.data is truthy, it calls authUserMutation? That implies if user exists?
            // "if (data.data.data)" might just check if object exists.
            // But later: "if (authData && authData.exists === false)" shows inputs.
            // So if `exists` is true, it proceeds to login. If `exists` is false, it shows signup form.

            const userData = res.data?.data;
            if (userData?.exists) {
                // User exists, login
                await auth.authenticate({ email });
                setView("otp");
                setCooldown(180);
            } else {
                // User doesn't exist, show registration fields
                setView("details");
            }
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || "Error checking user");
        } finally {
            setLoading(false);
        }
    };

    const handleSignUp = async (e) => {
        e.preventDefault();
        if (loading) return;
        setLoading(true);
        setError(null);

        const selectedCountry = countries.find(
            (country) => country.value === formData.country
        );
        const countryCode = selectedCountry?.phone || "";
        // simple validation to ensure we just send digits? Reference just concatenates.
        const fullPhoneNumber = "+" + countryCode + formData.phoneNumber;

        try {
            await auth.authenticate({
                email,
                name: formData.fullName,
                phone: fullPhoneNumber,
            });
            setView("otp");
            setOtpValues(Array(6).fill(""));
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (loading) return;
        if (otp.length !== 6) {
            setError("Please enter the complete 6-digit code");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            await auth.verify({ email, otp });
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || "Invalid OTP code");
            // Do not clear OTP values on error
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (cooldown > 0) return;
        setLoading(true);
        try {
            await auth.resendOtp({ email });
            setCooldown(180);
            setOtpValues(Array(6).fill(""));
        } catch (err) {
            setError(err.response?.data?.message || "Failed to resend code");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="tw-flex tw-flex-col tw-h-full tw-font-manrope tw-bg-white">
            <div className="tw-flex-1 tw-flex tw-flex-col tw-justify-center tw-px-8 tw-py-6">
                <div className="tw-flex tw-flex-col tw-items-center tw-mb-8">
                    <AnimatePresence mode="wait">
                        {view === "otp" || view === "details" ? (
                            <motion.div
                                key={view}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.2 }}
                                className="tw-text-center tw-w-full"
                            >
                                <div className="tw-relative tw-flex tw-items-center tw-justify-center tw-mb-6 tw-w-full">
                                    <button
                                        type="button"
                                        className="tw-absolute tw-left-0 tw-p-2 tw-rounded-full tw-border tw-border-gray-200 hover:tw-bg-gray-50 tw-transition-colors"
                                        onClick={() => {
                                            if (view === "otp") {
                                                setView("email");
                                                setOtpValues(Array(6).fill(""));
                                                setError(null);
                                            } else {
                                                setView("email");
                                                setError(null);
                                            }
                                        }}
                                    >
                                        <div className="tw-p-1 tw-rounded-full tw-bg-primary tw-text-white tw-flex tw-items-center tw-justify-center">
                                            <FaArrowLeft size={10} />
                                        </div>
                                    </button>
                                    <span className="tw-text-xl tw-font-bold tw-text-gray-900">
                                        {view === "otp"
                                            ? "Check your email"
                                            : "Create Account"}
                                    </span>
                                </div>
                                <p className="tw-text-sm tw-text-gray-500 tw-mt-2">
                                    {view === "otp"
                                        ? "We sent a code to "
                                        : "Sign up as "}
                                    <span className="tw-font-medium tw-text-gray-900">
                                        {email}
                                    </span>
                                </p>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="welcome"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{ duration: 0.2 }}
                                className="tw-text-center"
                            >
                                <span className="tw-text-2xl tw-font-bold tw-text-gray-800 tw-mb-2 tw-block">
                                    Welcome back
                                </span>
                                <p className="tw-text-sm tw-text-gray-500 tw-leading-relaxed">
                                    Sign in to save your history and sync
                                    conversations across devices, or continue as
                                    a guest.
                                </p>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <div className="tw-w-full">
                    {/* Error Message */}
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="tw-mb-6 tw-p-3 tw-bg-red-50 tw-border tw-border-red-100 tw-text-red-600 tw-text-sm tw-rounded-xl tw-text-center tw-font-medium"
                        >
                            {error}
                        </motion.div>
                    )}

                    {view === "otp" ? (
                        <motion.div
                            className="tw-flex tw-flex-col tw-gap-6"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.2 }}
                        >
                            <div className="tw-flex tw-gap-2 tw-justify-center tw-w-full">
                                {Array.from({ length: 6 }).map((_, index) => (
                                    <input
                                        key={index}
                                        className="tw-w-11 tw-h-14 tw-rounded-xl tw-bg-gray-50 tw-border-transparent focus:tw-bg-white focus:tw-border-primary/30 focus:tw-ring-4 focus:tw-ring-primary/10 tw-text-xl tw-text-center tw-font-bold tw-text-gray-900 tw-transition-all tw-outline-none"
                                        inputMode="numeric"
                                        type="text"
                                        maxLength={1}
                                        value={otpValues[index]}
                                        onChange={handleOtpBoxChange(index)}
                                        onKeyDown={handleOtpBoxKeyDown(index)}
                                        onPaste={
                                            index === 0
                                                ? handleOtpBoxPaste
                                                : undefined
                                        }
                                        ref={(el) =>
                                            (otpRefs.current[index] = el)
                                        }
                                    />
                                ))}
                            </div>

                            <span
                                role="button"
                                onClick={handleVerifyOtp}
                                className={`tw-w-full tw-bg-primary hover:tw-bg-primary/90 tw-text-white tw-rounded-lg tw-py-3 tw-text-sm tw-font-semibold tw-transition-colors tw-flex tw-items-center tw-justify-center focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-1 focus:tw-ring-primary tw-border-0 ${
                                    loading
                                        ? "tw-opacity-50 tw-cursor-not-allowed"
                                        : "tw-cursor-pointer"
                                }`}
                            >
                                {loading ? (
                                    <div className="tw-w-5 tw-h-5 tw-border-2 tw-border-white/30 tw-border-t-white tw-rounded-full tw-animate-spin" />
                                ) : (
                                    "Verify Code"
                                )}
                            </span>

                            <div className="tw-text-center">
                                {cooldown > 0 ? (
                                    <span className="tw-text-sm tw-text-gray-400">
                                        Resend code in {formatTime(cooldown)}
                                    </span>
                                ) : (
                                    <span
                                        role="button"
                                        onClick={
                                            !loading ? handleResend : undefined
                                        }
                                        className={`tw-text-sm tw-font-medium tw-text-gray-500 hover:tw-text-gray-900 tw-transition-colors ${
                                            loading
                                                ? "tw-opacity-50 tw-cursor-not-allowed"
                                                : "tw-cursor-pointer"
                                        }`}
                                    >
                                        Resend code
                                    </span>
                                )}
                            </div>
                        </motion.div>
                    ) : (
                        <form
                            className="tw-flex tw-flex-col tw-gap-4"
                            onSubmit={
                                view === "details"
                                    ? handleSignUp
                                    : handleCheckUser
                            }
                        >
                            {/* Google Login */}
                            {view === "email" && (
                                <>
                                    <span
                                        role="button"
                                        onClick={
                                            !isGoogleLoading && !loading
                                                ? handleGoogleLogin
                                                : undefined
                                        }
                                        className={`tw-w-full tw-bg-white tw-border tw-border-gray-200 tw-text-gray-700 tw-rounded-xl tw-py-3 tw-text-sm tw-font-semibold tw-transition-all tw-flex tw-items-center tw-justify-center tw-gap-2.5 hover:tw-bg-gray-50 hover:tw-shadow-sm active:tw-scale-[0.98] tw-relative ${
                                            isGoogleLoading || loading
                                                ? "tw-opacity-60 tw-cursor-not-allowed"
                                                : "tw-cursor-pointer"
                                        }`}
                                    >
                                        {isGoogleLoading ? (
                                            <div className="tw-w-5 tw-h-5 tw-border-2 tw-border-gray-300 tw-border-t-gray-600 tw-rounded-full tw-animate-spin" />
                                        ) : (
                                            <>
                                                <FcGoogle size={20} />
                                                <span>
                                                    Continue with Google
                                                </span>
                                            </>
                                        )}
                                    </span>

                                    <div className="tw-flex tw-items-center tw-gap-3 tw-py-1">
                                        <div className="tw-flex-1 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-gray-200 tw-to-gray-200" />
                                        <span className="tw-text-xs tw-font-medium tw-text-gray-400 tw-uppercase tw-tracking-widest">
                                            or
                                        </span>
                                        <div className="tw-flex-1 tw-h-px tw-bg-gradient-to-l tw-from-transparent tw-via-gray-200 tw-to-gray-200" />
                                    </div>
                                </>
                            )}

                            {/* Inputs with modern minimal style */}
                            {view === "details" && (
                                <>
                                    <div className="tw-space-y-1.5 animation-slide-up">
                                        <label className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-ml-1">
                                            Full Name
                                        </label>
                                        <input
                                            className="tw-w-full tw-px-4 tw-py-3 tw-rounded-xl tw-bg-gray-50 tw-border-transparent focus:tw-bg-white focus:tw-border-primary/30 focus:tw-ring-4 focus:tw-ring-primary/10 tw-text-sm tw-font-medium tw-text-gray-900 placeholder:tw-text-gray-400 tw-transition-all tw-outline-none"
                                            placeholder="John Doe"
                                            type="text"
                                            required
                                            value={formData.fullName}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    fullName: e.target.value,
                                                })
                                            }
                                        />
                                    </div>
                                    <div className="tw-space-y-1.5 animation-slide-up">
                                        <label className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-ml-1">
                                            Phone Number
                                        </label>
                                        <div className="tw-flex tw-items-center tw-w-full tw-rounded-xl tw-bg-gray-50 tw-border tw-border-transparent focus-within:tw-bg-white focus-within:tw-border-primary/30 focus-within:tw-ring-4 focus-within:tw-ring-primary/10 tw-transition-all">
                                            <div className="tw-w-[140px]">
                                                <Select
                                                    value={formData.country}
                                                    onValueChange={(value) =>
                                                        setFormData({
                                                            ...formData,
                                                            country: value,
                                                        })
                                                    }
                                                >
                                                    <SelectTrigger className="tw-h-full tw-border-0 tw-bg-transparent tw-shadow-none focus:tw-ring-0 hover:tw-bg-transparent">
                                                        <SelectValue placeholder="Country" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {countries.map(
                                                            (country) => (
                                                                <SelectItem
                                                                    key={
                                                                        country.value
                                                                    }
                                                                    value={
                                                                        country.value
                                                                    }
                                                                >
                                                                    <div className="tw-flex tw-items-center tw-gap-2">
                                                                        <span className="tw-text-lg">
                                                                            {
                                                                                country.emoji
                                                                            }
                                                                        </span>
                                                                        <span className="tw-text-gray-600">
                                                                            +
                                                                            {
                                                                                country.phone
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                </SelectItem>
                                                            )
                                                        )}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="tw-w-px tw-h-6 tw-bg-gray-200 tw-mx-1" />
                                            <input
                                                className="tw-flex-1 tw-px-4 tw-py-3 tw-bg-transparent tw-border-0 focus:tw-ring-0 tw-text-sm tw-font-medium tw-text-gray-900 placeholder:tw-text-gray-400 tw-outline-none"
                                                placeholder="(555) 000-0000"
                                                type="tel"
                                                required
                                                value={formData.phoneNumber}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        phoneNumber:
                                                            e.target.value.replace(
                                                                /\D/g,
                                                                ""
                                                            ),
                                                    })
                                                }
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            {view !== "details" && (
                                <div className="tw-space-y-1.5">
                                    <label className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-ml-1">
                                        Email Address
                                    </label>
                                    <input
                                        className="tw-w-full tw-px-4 tw-py-3 tw-rounded-xl tw-bg-gray-50 tw-border-transparent focus:tw-bg-white focus:tw-border-primary/30 focus:tw-ring-4 focus:tw-ring-primary/10 tw-text-sm tw-font-medium tw-text-gray-900 placeholder:tw-text-gray-400 disabled:tw-opacity-60 tw-transition-all tw-outline-none"
                                        placeholder="name@example.com"
                                        type="email"
                                        required
                                        disabled={view === "details"}
                                        value={email}
                                        onChange={(e) =>
                                            setEmail(e.target.value)
                                        }
                                    />
                                </div>
                            )}

                            <div className="tw-pt-2 tw-flex tw-flex-col tw-gap-3">
                                <span
                                    role="button"
                                    onClick={(e) => {
                                        if (loading) return;
                                        if (view === "details") handleSignUp(e);
                                        else handleCheckUser(e);
                                    }}
                                    className={`tw-w-full tw-bg-primary hover:tw-bg-primary/90 tw-text-white tw-rounded-lg tw-py-3 tw-text-sm tw-font-semibold tw-transition-colors tw-flex tw-items-center tw-justify-center focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-1 focus:tw-ring-primary tw-border-0 ${
                                        loading
                                            ? "tw-opacity-50 tw-cursor-not-allowed"
                                            : "tw-cursor-pointer"
                                    }`}
                                >
                                    {loading ? (
                                        <div className="tw-flex tw-items-center tw-gap-2">
                                            <div className="tw-w-4 tw-h-4 tw-border-2 tw-border-white/30 tw-border-t-white tw-rounded-full tw-animate-spin" />
                                            <span>Processing...</span>
                                        </div>
                                    ) : view === "details" ? (
                                        "Create Account"
                                    ) : (
                                        "Continue with Email"
                                    )}
                                </span>

                                {view === "details" ? (
                                    <span
                                        role="button"
                                        onClick={() => setView("email")}
                                        className="tw-text-sm tw-text-center tw-font-medium tw-text-gray-500 hover:tw-text-gray-900 tw-transition-colors tw-bg-transparent tw-border-0 tw-cursor-pointer"
                                    >
                                        Use a different email
                                    </span>
                                ) : (
                                    <span
                                        role="button"
                                        onClick={onSkip}
                                        className="tw-w-full tw-py-2.5 tw-text-center tw-text-sm tw-font-medium tw-text-gray-500 hover:tw-text-gray-900 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-200 tw-bg-transparent tw-border-0 tw-cursor-pointer"
                                    >
                                        Skip for now
                                    </span>
                                )}
                            </div>
                        </form>
                    )}
                </div>
            </div>

            {/* Footer / Trust Badge (Optional, good for minimal professional look) */}
            <div className="tw-py-4 tw-text-center tw-border-t tw-border-gray-50">
                <p className="tw-text-[10px] tw-text-gray-300 tw-font-medium">
                    SECURED BY{" "}
                    <a
                        href="https://salesive.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tw-text-gray-300 hover:tw-text-gray-500 hover:tw-underline tw-transition-colors"
                    >
                        SALESIVE
                    </a>
                </p>
            </div>
        </div>
    );
}
